import React, { useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
} from "@mui/material";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useNavigate } from "react-router-dom";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";

/* ============================================================================
 * Logout confirmation
 * ----------------------------------------------------------------------------
 * Signing out is now reachable from three places — the Daily Tick sidebar, the
 * pawn rail and the travel dashboard header — because a store that opens
 * straight into its own module never passes through the Daily Tick shell that
 * used to be the only way out (see getStoreHome).
 *
 * The hosts look nothing alike, so only the confirm step is shared: each one
 * renders its own trigger in its own skin and hands the "are you sure" over to
 * this. The clearing itself lives here so the three can never drift on WHAT
 * logging out means.
 *
 * `replace` on the way to the login screen: the pages behind us belong to a
 * session that no longer exists, so back must not walk into them.
 * ========================================================================== */
const LogoutConfirmDialog = ({ open, onClose }) => {
  const { clearLoginData } = useContext(AutoTaskAuthContext) || {};
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const handleConfirm = async () => {
    setSigningOut(true);
    try {
      await clearLoginData?.();
    } finally {
      setSigningOut(false);
    }
    onClose?.();
    navigate("/", { replace: true });
  };

  return (
    <Dialog
      open={open}
      onClose={signingOut ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="logout-dialog-title"
      aria-describedby="logout-dialog-description"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        id="logout-dialog-title"
        sx={{ display: "flex", alignItems: "center", gap: 1.25, pb: 1 }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(211,47,47,0.1)",
            flexShrink: 0,
          }}
        >
          <LogoutRoundedIcon sx={{ fontSize: 19, color: "#D32F2F" }} />
        </Box>
        <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
          Confirm Logout
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography id="logout-dialog-description" sx={{ fontSize: 14 }}>
          Are you sure you want to log out?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={signingOut}
          sx={{ color: "#008080", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={signingOut}
          variant="contained"
          color="error"
          sx={{ textTransform: "none" }}
        >
          {signingOut ? "Logging out…" : "Log out"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogoutConfirmDialog;
